import React, { useState, useRef, useEffect } from 'react';
import _ from 'lodash';
import cx from 'classnames';
import Draggable from 'react-draggable';

import { FileType } from 'constants/constants';
import { rectDiff, gridPosition } from 'utils/geometry';
import { backgroundImage, getTypeFromUrl } from 'utils/media';

import { getSceneLength, compareSettings, getSceneStyle, isTransparent } from 'store/models/Scene';

import { AddButton, EditIconButton, DeleteIconButton, RefreshIconButton, IconButtonList } from 'components/Button';
import { AsteriskIcon } from 'components/Svg';
import { EmptyItem } from 'components/EmptyItem';
import { StatItem, StatItemList, StatItemMoney, StatItemNumber, StatItemPercent, StatItemSeconds, StatItemHours, StatItemMinutes } from 'components/Stats';
import Tooltip from 'components/Tooltip';
import { LoadingStatItem } from 'components/Loading';






// ====================
// SCENE ITEM
// ====================

export const SceneItem = React.forwardRef((p, ref) => {
  let { 
    scene, 
    loading, 
    size, 
    className, 
    baseSettings, 
    settings, 
    showLength = false, showCustom = false, showStyle = false, onClick, ...props } = p;

  let videoElm = useRef();
  let url = scene?.url;
  let onSceneClick = () => onClick?.(scene._id, scene);
  let length = getSceneLength(baseSettings, settings);
  let style = showStyle ? getSceneStyle(settings, baseSettings) : {};
  let isCustom = !compareSettings(settings, baseSettings);
  let isTrans = isTransparent(settings, baseSettings)
  let onLoad = () => {
    videoElm.current.currentTime = (videoElm.current.duration ?? 0) * .10;
  }
  
  let type = getTypeFromUrl(url);
  let thumbnail;
  switch(type) {
    case FileType.VIDEO:
      thumbnail = (
        <div className="thumb video">
          <video controls={false} ref={videoElm} onLoadedData={onLoad}>
            <source src={url} />
          </video>
        </div>
      );
      break;
    default:
      thumbnail = <div className="thumb image" style={{ backgroundImage: `url('${url}')` }} />;
  }

  return (
    <div 
      className={cx('g-scene-item', size, className, { 'has-click': _.isFunction(onClick), 'slide-no-background': showStyle && isTrans })} 
      onClick={onSceneClick}
      style={style}
      ref={ref}>
      { thumbnail }
      { showLength && length &&
      <div className="length">{length / 1000}s</div>}
      { showCustom && isCustom && 
      <div className="custom"><AsteriskIcon /></div>}
    </div>
  )
});





// ====================
// EMPTY
// ====================
export const SceneItemEmpty = ({ onAdd, className, size, ...props }) => (
  <EmptyItem className={cx('empty-link', className, size)}>
    <h3>Add Slides to display across channels</h3>
    <h4>Give streamers their unique slideshow code and all your slides will rotate on-stream</h4>
    <AddButton onClick={onAdd}>Add Slide</AddButton>
  </EmptyItem>)




// ====================
// SCENE LIST
// ====================
export const SceneList = React.forwardRef((p, ref) => {
  let { scenes, onRenderItem, dragTarget, className, wrap, align, size, showEmpty = true, sceneSettings, onAdd, onSceneSelect, ...props } = p;
  
  if(_.isEmpty(scenes) && showEmpty) {
    return <SceneItemEmpty onAdd={onAdd} />;;
  }
  onRenderItem = onRenderItem ?? (v => v);
  return (
    <div ref={ref} className={cx('g-scene-list', align, size, className, { wrap })}>
      { _.map(scenes, (scene, idx) => onRenderItem(
        <SceneItem
          key={scene._id}
          scene={scene}
          size={size}
          settings={sceneSettings?.[scene._id]?.settings ?? sceneSettings?.[scene._id]}
          onClick={onSceneSelect}
          {...props} />, scene, idx))}
    </div>);
})

export const SceneListRow = React.forwardRef((props, ref) =>
  <SceneList ref={ref} {...props} align="row" />)






// ====================
// REORDERABLE LIST
// ====================
export const DraggableSceneItem = ({ scene, dragTarget, children, onDragStart, onDrag, onDragStop, ...props }) => {
  let [dragging, setDragging] = useState(false);
  let [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const wrapperRef = useRef(null);

  let onDragStartItem = e => {
    e.stopPropagation();
    e.preventDefault();
  }

  let onDragItem = (e, data) => {
    e.stopPropagation();
    e.preventDefault();

    if(!dragging && (Math.abs(data.x) > 5 || Math.abs(data.y) > 5)) {
      setDragging(true);
      data = _.set(data, 'parent', wrapperRef.current);
      onDragStart?.(data, scene._id);
    } 
    else if(dragging) {
      data = _.set(data, 'parent', wrapperRef.current);
      onDrag?.(data);
    }
  }

  let onDragStopItem = (e, data) => {
    if(!dragging)
      return;

    e.stopPropagation();
    e.preventDefault();

    setDragPosition({ x: 0, y: 0 });
    setDragging(false);
    data = _.set(data, 'parent', wrapperRef.current);
    onDragStop?.(data, scene._id);
  }

  return (
    <div 
      ref={wrapperRef}
      className={cx("g-scene-item-drag-wrapper", { dragging,'drag-target': dragTarget })}>
      <Draggable 
        onStart={onDragStartItem}
        onStop={onDragStopItem}
        onDrag={onDragItem}
        position={dragPosition}>
        <div>{children}</div>
      </Draggable>
      <div className="ghost-image"><div style={backgroundImage(scene.url)}></div></div>
    </div>)
}


export const ReorderableSceneList = ({scenes, onDragStart, onOrderChange, onSceneSelect,...props}) => {
  let listRef = useRef(null);
  let [dragging, setDragging] = useState(false);
  let [overIndex, setOverIndex] = useState(null);
  let [order, setOrder] = useState({ row: 0, column: 0 });


  let onDragStartList = () => {
    setDragging(true);
    onDragStart?.();
  }

  let onDragList = (data) => {
    let parentRect = data.parent.getBoundingClientRect();
    let elmRect = data.node.getBoundingClientRect();
    let contRect = listRef.current.getBoundingClientRect();

    let diff = rectDiff(contRect, elmRect);
    let order = gridPosition(diff.x, diff.y, parentRect.width, parentRect.height, contRect.width, contRect.height);
    let overIndex = (order.columnCount * (order.row - 1)) + (order.column - 1);
    overIndex = Math.min(_.size(scenes) - 1, overIndex);
    setOverIndex(overIndex);
    setOrder(order)
  }

  let onDragStopList = (data, sceneId) => {
    if(!dragging) 
      return;

    setDragging(true);
    setOverIndex(null);
    onOrderChange?.(sceneId, overIndex);
  }

  let onSceneSelectIntercept = (...args) => {
    if(dragging)
      return setDragging(false);
    onSceneSelect?.(...args);
  }

  return (
    <SceneListRow 
      ref={listRef}
      scenes={scenes}
      onSceneSelect={onSceneSelectIntercept}
      onRenderItem={(item, scene, idx) => (
        <DraggableSceneItem 
          key={scene._id}
          scene={scene}
          dragTarget={dragging && idx === overIndex}
          onDragStart={onDragStartList}
          onDrag={onDragList}
          onDragStop={onDragStopList}>
          {item}
        </DraggableSceneItem>
      )}
      {...props} />
  )
}








// ====================
// STATS LIST
// ====================
export const SceneStatItem = ({ scene, stats, active, loading, ...props}) => {
  return (
    <div className="g-scene-stats-item">
      <div className="scene-thumb-wrap">
        <SceneItem scene={scene} />
      </div>
      <StatItemList>
        <StatItemNumber label="Impressions" value={stats?.sceneViews} />
        <StatItemNumber label="Count" value={stats?.sceneCount} />  
        <StatItemSeconds label="Length" value={stats?.sceneAvgLength} />
        <StatItemHours label="Time Viewed" value={stats?.sceneViewedMinutes} />
        <StatItemHours label="Airtime" value={stats?.sceneAirtimeMinutes}/>
      </StatItemList>
      <Tooltip tooltip={active ? "Currently active in a campaign" : "Not active in any campaign"}>
        <div className={cx("status", { active })} />
      </Tooltip>
    </div>)
}


export const SceneStatList = ({ scenes, sceneStats, activeScenes, loading, loadingCount = 4, className, children, ...props }) => {
  if(!loading && _.isEmpty(scenes)) 
    return (
      <EmptyItem>
        <p>No slides served for this time period</p>
      </EmptyItem>)

  return (
    <div className="g-scene-stats-list">
      { loading && 
        _.map(_.fill(Array(loadingCount), '0'), (v, idx) => <LoadingStatItem key={idx} className="g-scene-stats-item" showThumb={true} />) }

      { !loading && _.map(scenes, scene => 
        <SceneStatItem 
          key={scene._id} 
          scene={scene} 
          stats={sceneStats?.[scene._id]}
          active={_.includes(activeScenes, scene._id)} />)}
    </div>);
}






// ====================
// MANAGE LIST
// ====================
export const SceneManageItem = ({ scene, loading, onEdit, onDelete, onRestore, onSceneClick, ...props}) => {
  return (
    <div className="g-scene-manage-item">
      <div className="scene-item-wrap">
        <div className="scene-thumb-wrap">
          <SceneItem scene={scene} />
        </div>
        <div className="manage-actions">
          <IconButtonList>
            { onDelete &&
            <Tooltip tooltip={`Archive this slide`}>
              <DeleteIconButton size="m" onClick={() => onDelete(scene._id, scene)} />
            </Tooltip>}
            { onRestore &&
            <Tooltip tooltip={`Restore this slide back to your library`}>
              <RefreshIconButton size="m" onClick={() => onRestore(scene._id, scene)} />
            </Tooltip>}
          </IconButtonList>
        </div>
        </div>
    </div>)
}


export const SceneManageList = ({ scenes, loading, loadingCount = 4, className, children, ...props }) => {
  if(!loading && _.isEmpty(scenes)) 
    return (
      <EmptyItem>
        <p>Your account does not have any slides yet</p>
      </EmptyItem>)

  return (
    <div className="g-scene-manage-list">
      { loading && 
        _.map(_.fill(Array(loadingCount), '0'), (v, idx) => <LoadingStatItem key={idx} className="g-scene-stats-item" showThumb={true} />) }

      { !loading && _.map(scenes, scene => 
        <SceneManageItem 
          key={scene._id} 
          scene={scene} 
          {...props} />)}
    </div>);
}